import React, { ReactNode, useEffect } from 'react';
import { StyleSheet } from 'react-native';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { isWeb } from 'shared/constants/platform';
import theme from 'shared/styles/theme';

export interface ScreenTransitionOverlayProps {
  isLoading: boolean;
  LoadingComponent: ReactNode;
  ErrorComponent: ReactNode | null | undefined;
  isTabPage?: boolean;
}

/**
 * this overlay animates the presence of a full screen loading or
 * error state based on the props provided.
 */
function ScreenTransitionOverlay({
  isLoading,
  LoadingComponent,
  ErrorComponent,
  isTabPage = false,
}: ScreenTransitionOverlayProps) {
  const opacity = useSharedValue(isLoading || !!ErrorComponent ? 1 : 0);

  const hasErrors = !!ErrorComponent;
  const isVisible = hasErrors || isLoading;

  let overlayContent = LoadingComponent;
  if (hasErrors && !isLoading) {
    overlayContent = ErrorComponent;
  }

  // Update the opacity when visibility changes
  useEffect(() => {
    opacity.value = withTiming(isVisible ? 1 : 0, { duration: 100 });
  }, [isVisible, opacity]);

  // Use animated styles instead of direct style manipulation
  const animatedStyle = useAnimatedStyle(() => {
    return {
      opacity: opacity.value,
      // Only render when opacity is greater than 0.01
      display: opacity.value < 0.01 ? 'none' : 'flex',
    };
  });

  return (
    <Animated.View
      style={[
        styles.overlayContainer,
        hasErrors && isTabPage && styles.tabErrorOverlayContainer,
        animatedStyle,
      ]}
    >
      {overlayContent}
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  overlayContainer: {
    alignItems: 'center',
    backgroundColor: theme.color.background.primary,
    height: '100%',
    justifyContent: 'center',
    left: 0,
    position: 'absolute',
    right: 0,
    top: 0,
    zIndex: 2,
  },
  tabErrorOverlayContainer: {
    backgroundColor: isWeb
      ? theme.color.background.primary
      : theme.color.background.surface,
  },
});

export default ScreenTransitionOverlay;
